import { useCallback, useEffect, useMemo } from 'react'
import { useSearchParams } from 'react-router-dom'
import { SeverityMap } from '../routes/dashboards/charts/_helpers'
import {
  type DashboardTimeGranularity,
  formatLocalDate,
  getDashboardTimezone,
  toIsoRangeFromDateOnly,
} from './dateUtils'

/**
 * Which date the range applies to. Absent means the audited date, so a URL
 * carrying no mode is a valid audited-date view.
 */
export type DashboardDateMode = 'dateAudited' | 'dateOfService'

type DashboardListFilters = {
  page?: number
  limit?: number
  fromDate?: string
  toDate?: string
  dateMode?: DashboardDateMode
  noDateOfService?: boolean
  /**
   * Range the user had before the no-date-of-service view moved From/To to the
   * last month of those records, so Clear can put them back.
   */
  priorFromDate?: string
  priorToDate?: string
  location?: string
  program?: string
  severity?: number
  userEmail?: string
  patientMrn?: string
  measures?: string[]
  sortBy?: string
  sortOrder?: 'asc' | 'desc'
}

type LocalFilters = {
  selectedSeverity?: keyof typeof SeverityMap | null
  selectedDateRange?: { from: string; to: string }
  graphGranularityDateRange?: { from: string; to: string } | null
  detailViewClinicianEmail?: string | null
  detailViewPatientMrn?: string | null
  zoomHistory?: Array<{
    granularity: DashboardTimeGranularity
    range: { from: string; to: string }
    graphRange?: { from: string; to: string } | null
  }>
}

const parseZoomHistoryFromURL = (searchParams: URLSearchParams): LocalFilters['zoomHistory'] => {
  const raw = searchParams.get('zoomHistory')
  if (!raw) return []

  try {
    const parsed = JSON.parse(decodeURIComponent(raw))
    return Array.isArray(parsed) ? parsed : []
  } catch {
    return []
  }
}

const parseFiltersFromURL = (
  searchParams: URLSearchParams
): DashboardListFilters & LocalFilters => {
  const graphRangeFrom = searchParams.get('graphRangeFrom')
  const graphRangeTo = searchParams.get('graphRangeTo')

  return {
    page: searchParams.get('page') ? parseInt(searchParams.get('page')!) : undefined,
    limit: searchParams.get('limit') ? parseInt(searchParams.get('limit')!) : undefined,
    fromDate: searchParams.get('fromDate') ?? undefined,
    toDate: searchParams.get('toDate') ?? undefined,
    dateMode: searchParams.get('dateMode') === 'dateOfService' ? 'dateOfService' : undefined,
    noDateOfService: searchParams.get('noDateOfService') === 'true' ? true : undefined,
    priorFromDate: searchParams.get('priorFromDate') ?? undefined,
    priorToDate: searchParams.get('priorToDate') ?? undefined,
    location: searchParams.get('location') ?? undefined,
    program: searchParams.get('program') ?? undefined,
    severity: searchParams.get('severity') ? parseInt(searchParams.get('severity')!) : undefined,
    userEmail: searchParams.get('userEmail') ?? undefined,
    patientMrn: searchParams.get('patientMrn') ?? undefined,
    measures: searchParams.get('measures') ? searchParams.get('measures')!.split(',') : undefined,
    sortBy: searchParams.get('sortBy') ?? undefined,
    sortOrder: searchParams.get('sortOrder') as 'asc' | 'desc' | undefined,
    selectedSeverity: searchParams.get('selectedSeverity')
      ? (parseInt(searchParams.get('selectedSeverity')!) as unknown as keyof typeof SeverityMap)
      : null,
    selectedDateRange: {
      from: searchParams.get('selectedFromDate') ?? '',
      to: searchParams.get('selectedToDate') ?? '',
    },
    graphGranularityDateRange:
      graphRangeFrom && graphRangeTo
        ? {
            from: graphRangeFrom,
            to: graphRangeTo,
          }
        : null,
    detailViewClinicianEmail: searchParams.get('detailViewClinicianEmail') ?? null,
    detailViewPatientMrn: searchParams.get('detailViewPatientMrn') ?? null,
    zoomHistory: parseZoomHistoryFromURL(searchParams),
  }
}

const serializeFiltersToURL = (filters: DashboardListFilters & LocalFilters): URLSearchParams => {
  const params = new URLSearchParams()
  if (filters.page) params.set('page', filters.page.toString())
  if (filters.limit) params.set('limit', filters.limit.toString())
  if (filters.fromDate) params.set('fromDate', filters.fromDate)
  if (filters.toDate) params.set('toDate', filters.toDate)
  if (filters.dateMode === 'dateOfService') params.set('dateMode', filters.dateMode)
  if (filters.noDateOfService) params.set('noDateOfService', 'true')
  if (filters.priorFromDate) params.set('priorFromDate', filters.priorFromDate)
  if (filters.priorToDate) params.set('priorToDate', filters.priorToDate)
  if (filters.location) params.set('location', filters.location)
  if (filters.program) params.set('program', filters.program)
  if (filters.severity) params.set('severity', filters.severity.toString())
  if (filters.userEmail) params.set('userEmail', filters.userEmail)
  if (filters.patientMrn) params.set('patientMrn', filters.patientMrn)
  if (filters.measures) params.set('measures', filters.measures.join(','))
  if (filters.sortBy) params.set('sortBy', filters.sortBy)
  if (filters.sortOrder) params.set('sortOrder', filters.sortOrder)
  if (filters.selectedSeverity) params.set('selectedSeverity', filters.selectedSeverity.toString())
  if (filters.selectedDateRange?.from)
    params.set('selectedFromDate', filters.selectedDateRange.from)
  if (filters.selectedDateRange?.to) params.set('selectedToDate', filters.selectedDateRange.to)
  if (filters.graphGranularityDateRange?.from)
    params.set('graphRangeFrom', filters.graphGranularityDateRange.from)
  if (filters.graphGranularityDateRange?.to)
    params.set('graphRangeTo', filters.graphGranularityDateRange.to)
  if (filters.detailViewClinicianEmail)
    params.set('detailViewClinicianEmail', filters.detailViewClinicianEmail)
  if (filters.detailViewPatientMrn) params.set('detailViewPatientMrn', filters.detailViewPatientMrn)
  if (filters.zoomHistory !== undefined)
    params.set('zoomHistory', encodeURIComponent(JSON.stringify(filters.zoomHistory)))

  return params
}

const getDefaultDateRange = () => {
  const to = new Date()
  const from = new Date()
  from.setMonth(from.getMonth() - 1)

  return {
    from: formatLocalDate(from),
    to: formatLocalDate(to),
  }
}

/**
 * Configured to work with only one type of pagination. Use defaultFilters to pass page and limit filters to enable the page based pagination,
 * otherwise default date range will be used viz. from now to past month.
 * @param defaultFilters - Default filters to use if no filters are set in the URL
 * @returns - Filters object and functions to update and reset filters
 */
const useDashboardListFilters = (defaultFilters?: Partial<DashboardListFilters & LocalFilters>) => {
  const [searchParams, setSearchParams] = useSearchParams()

  // Parse URL params into typed filter object
  const filters = useMemo(() => parseFiltersFromURL(searchParams), [searchParams])

  // Update URL when filters change while preserving scroll position
  const updateFilters = useCallback(
    (newFilters: Partial<DashboardListFilters & LocalFilters>) => {
      setSearchParams(
        (currentSearchParams) => {
          const currentFilters = parseFiltersFromURL(currentSearchParams)
          const nextFilters = {
            ...currentFilters,
            ...newFilters,
          }
          return serializeFiltersToURL(nextFilters)
        },
        { replace: true }
      )
    },
    [setSearchParams]
  )

  const resetFilters = useCallback(() => {
    setSearchParams(new URLSearchParams(), { replace: true })
  }, [setSearchParams])

  useEffect(() => {
    const nextFilters: Partial<DashboardListFilters & LocalFilters> = {}
    const shouldApplyDefaultFilters = !!defaultFilters && (!filters.page || !filters.limit)

    if (shouldApplyDefaultFilters && defaultFilters) {
      Object.assign(nextFilters, defaultFilters)
    }

    // The /alerts page pages through the full result set and opts out of the default
    // date range. It's the only caller that declares page/limit via defaultFilters,
    // so we key off that alone. Views that only use URL pagination for a table
    // (e.g. /dashboard/allalerts) still get the default date range.
    const optsOutOfDefaultDateRange =
      defaultFilters?.page !== undefined || defaultFilters?.limit !== undefined

    const mergedFromDate = nextFilters.fromDate ?? filters.fromDate
    const mergedToDate = nextFilters.toDate ?? filters.toDate
    const existingRange = toIsoRangeFromDateOnly(
      mergedFromDate,
      mergedToDate,
      getDashboardTimezone()
    )
    const hasValidDateRange = !!existingRange.fromDate && !!existingRange.toDate

    if (!optsOutOfDefaultDateRange && !hasValidDateRange) {
      const { from, to } = getDefaultDateRange()
      const range = toIsoRangeFromDateOnly(from, to, getDashboardTimezone())
      if (!range.fromDate || !range.toDate) return

      // Set default date range to past month if not set in URL
      Object.assign(nextFilters, {
        fromDate: from,
        toDate: to,
        selectedDateRange: {
          from: range.fromDate,
          to: range.toDate,
        },
        detailViewClinicianEmail: null,
        detailViewPatientMrn: null,
        zoomHistory: [],
      })
    }

    if (Object.keys(nextFilters).length > 0) {
      updateFilters(nextFilters)
    }
  }, [filters.fromDate, filters.toDate, filters.page, filters.limit, updateFilters, defaultFilters])

  return {
    filters,
    updateFilters,
    resetFilters,
  }
}

export default useDashboardListFilters
